/**
 * Pareto Tab — Análisis 80/20 de causas de fallas y demoras.
 * Gráfico combinado: barras (downtime por causa) + curva acumulada + línea 80%.
 * Filtros: rango de fechas + equipo + catálogo curable de causas.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChartBar, FilePdf, ArrowsClockwise, Gear, Plus, Trash, Sparkle, Scales,
} from "@phosphor-icons/react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceLine, Cell, BarChart,
} from "recharts";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function ParetoTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-slate-300 rounded p-2 text-xs shadow-lg">
      <p className="font-bold text-slate-800">{p.full}</p>
      <p>Downtime: <b>{p.downtime.toFixed(1)} h</b></p>
      <p>% acumulado: <b>{p.cum.toFixed(1)}%</b></p>
      {p.vital && <p className="text-amber-700 font-bold">★ Causa vital (Pareto)</p>}
    </div>
  );
}

export default function ParetoTab({ equipment }) {
  const today = new Date().toISOString().slice(0, 10);
  const start = new Date(); start.setMonth(start.getMonth() - 3);
  const [range, setRange] = useState({ from: start.toISOString().slice(0, 10), to: today });
  return (
    <div className="space-y-4" data-testid="mnt-pareto">
      <Tabs defaultValue="single">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="single" data-testid="pareto-mode-single" className="gap-1.5">
            <ChartBar size={14} weight="bold" /> Pareto único
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="pareto-mode-compare" className="gap-1.5">
            <Scales size={14} weight="bold" /> Comparativa Turnos (A/B/C)
          </TabsTrigger>
        </TabsList>
        <TabsContent value="single" className="mt-4">
          <ParetoSingle equipment={equipment} range={range} setRange={setRange} />
        </TabsContent>
        <TabsContent value="compare" className="mt-4">
          <ParetoCompare equipment={equipment} range={range} setRange={setRange} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ParetoSingle({ equipment, range, setRange }) {
  const [equipmentId, setEquipmentId] = useState("");
  const [crew, setCrew] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { from: range.from, to: range.to };
      if (equipmentId) params.equipment_id = equipmentId;
      if (crew) params.crew = crew;
      const r = await api.get("/maintenance/pareto/failures", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [range, equipmentId, crew]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const params = { from: range.from, to: range.to };
      if (equipmentId) params.equipment_id = equipmentId;
      if (crew) params.crew = crew;
      const r = await api.get("/maintenance/pareto/failures/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pareto-mantenimiento-${range.from}-a-${range.to}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF Pareto descargado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const chartData = useMemo(() =>
    (data?.rows || []).map((r) => ({
      name: r.category.length > 18 ? r.category.slice(0, 16) + "…" : r.category,
      full: r.category,
      downtime: r.downtime_h,
      cum: r.cum_pct,
      vital: r.vital,
    })), [data]);

  return (
    <div className="space-y-4" data-testid="mnt-pareto-single">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                 className="h-9 w-40" data-testid="pareto-from" /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                 className="h-9 w-40" data-testid="pareto-to" /></div>
        <div>
          <Label className="overline-label">Equipo</Label>
          <Select value={equipmentId || "all"} onValueChange={(v) => setEquipmentId(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="pareto-equipment"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Toda la flota</SelectItem>
              {(equipment || []).map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline-label">Cuadrilla</Label>
          <Select value={crew || "all"} onValueChange={(v) => setCrew(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-32" data-testid="pareto-crew"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="A">Cuadrilla A</SelectItem>
              <SelectItem value="B">Cuadrilla B</SelectItem>
              <SelectItem value="C">Cuadrilla C</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="pareto-refresh" className="gap-1.5">
          <ArrowsClockwise size={14} weight="bold" /> Actualizar
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCatOpen(true)}
                data-testid="pareto-cat-btn" className="gap-1.5">
          <Gear size={14} weight="bold" /> Catálogo de causas
        </Button>
        <div className="flex-1" />
        <Button onClick={downloadPdf} data-testid="pareto-pdf-btn"
                className="bg-blue-900 hover:bg-blue-800 gap-1.5">
          <FilePdf size={14} weight="bold" /> Descargar PDF
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MicroKpi label="Eventos" value={data.totals?.events || 0} />
          <MicroKpi label="Downtime total (h)" value={(data.totals?.downtime_h || 0).toFixed(1)} />
          <MicroKpi label="Causas vitales" value={data.vital_few_count || 0}
                    sub="Concentran 80% del impacto" accent="amber" />
          <MicroKpi label="Costo estimado (USD)"
                    value={`$${(data.totals?.cost_usd || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`}
                    accent="emerald" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="font-heading text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <ChartBar size={16} weight="fill" className="text-blue-900" /> Diagrama de Pareto
        </p>
        {chartData.length === 0 ? (
          <p className="text-slate-400 italic py-10 text-center">Sin eventos en el rango seleccionado.</p>
        ) : (
          <div style={{ width: "100%", height: 380 }} data-testid="pareto-chart">
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 20, right: 40, left: 5, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={70}
                       tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }}
                       label={{ value: "Horas de downtime", angle: -90, position: "insideLeft",
                                style: { fontSize: 10, fill: "#64748B" } }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }}
                       label={{ value: "% acumulado", angle: 90, position: "insideRight",
                                style: { fontSize: 10, fill: "#64748B" } }} />
                <Tooltip content={<ParetoTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={80} yAxisId="right" stroke="#DC2626" strokeDasharray="4 3"
                                label={{ value: "80%", fill: "#DC2626", fontSize: 10, position: "right" }} />
                <Bar yAxisId="left" dataKey="downtime" name="Horas de downtime">
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.vital ? "#B45309" : "#64748B"} />
                  ))}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="cum" name="% acumulado"
                      stroke="#0F766E" strokeWidth={2} dot={{ r: 4, fill: "#0F766E" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Análisis */}
      {data?.analysis?.length > 0 && (
        <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-4"
             data-testid="pareto-analysis">
          <p className="font-heading text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
            <Sparkle size={16} weight="fill" /> Análisis del gráfico
          </p>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.analysis.map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-blue-900 font-bold">•</span>
                <span dangerouslySetInnerHTML={{ __html: a.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabla detallada */}
      {data?.rows?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="pareto-table">
            <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
              <tr>
                <th className="text-center px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Causa</th>
                <th className="text-center px-3 py-2">Eventos</th>
                <th className="text-center px-3 py-2">Downtime (h)</th>
                <th className="text-center px-3 py-2">%</th>
                <th className="text-center px-3 py-2">% Acum.</th>
                <th className="text-right px-3 py-2">Costo (USD)</th>
                <th className="text-left px-3 py-2">Ejemplos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => (
                <tr key={r.category} className={r.vital ? "bg-amber-50/50" : ""}
                    data-testid={`pareto-row-${r.rank}`}>
                  <td className="text-center px-3 py-2 font-bold">{r.rank}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">
                    {r.vital && <span className="text-amber-700 mr-1">★</span>}
                    {r.category}
                  </td>
                  <td className="text-center px-3 py-2">{r.events}</td>
                  <td className="text-center px-3 py-2">{r.downtime_h.toFixed(1)}</td>
                  <td className="text-center px-3 py-2">{r.pct.toFixed(1)}%</td>
                  <td className="text-center px-3 py-2 font-bold text-teal-700">{r.cum_pct.toFixed(1)}%</td>
                  <td className="text-right px-3 py-2">
                    {r.cost_usd > 0 ? `$${r.cost_usd.toLocaleString("es-MX", { maximumFractionDigits: 0 })}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {(r.examples || []).map((ex, i) => (<div key={i}>• {ex}</div>))}
                    {!r.examples?.length && <span className="italic">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DelayCatalogDialog open={catOpen} onOpenChange={setCatOpen} onSaved={load} />
    </div>
  );
}

/* =============== Comparativa por Turno A/B/C =============== */
function ParetoCompare({ equipment, range, setRange }) {
  const [equipmentId, setEquipmentId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { from: range.from, to: range.to };
      if (equipmentId) params.equipment_id = equipmentId;
      const r = await api.get("/maintenance/pareto/failures/compare", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [range, equipmentId]);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() =>
    (data?.rows || []).slice(0, 12).map((r) => ({
      name: r.category.length > 20 ? r.category.slice(0, 18) + "…" : r.category,
      full: r.category,
      A: r.A_h, B: r.B_h, C: r.C_h,
      total: r.total_h, worst: r.worst_crew,
    })), [data]);

  const totalsByCrew = data?.totals_by_crew || {};
  const crewColor = { A: "#1D4ED8", B: "#B45309", C: "#0F766E" };

  return (
    <div className="space-y-4" data-testid="pareto-compare">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                 className="h-9 w-40" data-testid="pareto-cmp-from" /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                 className="h-9 w-40" data-testid="pareto-cmp-to" /></div>
        <div>
          <Label className="overline-label">Equipo</Label>
          <Select value={equipmentId || "all"} onValueChange={(v) => setEquipmentId(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="pareto-cmp-equipment"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Toda la flota</SelectItem>
              {(equipment || []).map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="pareto-cmp-refresh" className="gap-1.5">
          <ArrowsClockwise size={14} weight="bold" /> Actualizar
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3">
          {["A", "B", "C"].map((c) => {
            const t = totalsByCrew[c] || {};
            return (
              <div key={c} className="rounded-lg border p-3"
                   style={{ borderColor: crewColor[c] + "40", background: crewColor[c] + "10" }}>
                <p className="text-[10px] uppercase font-bold tracking-wider"
                   style={{ color: crewColor[c] }}>Cuadrilla {c}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className="font-heading text-2xl font-black" style={{ color: crewColor[c] }}>
                    {(t.downtime_h || 0).toFixed(1)}
                  </p>
                  <p className="text-xs text-slate-500">h · {t.events || 0} eventos</p>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5">{t.categories || 0} causas distintas</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="font-heading text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Scales size={16} weight="fill" className="text-blue-900" /> Downtime por causa · Cuadrilla A vs B vs C
        </p>
        {chartData.length === 0 ? (
          <p className="text-slate-400 italic py-10 text-center">
            Sin eventos por turno en el rango. Asigna el campo <b>crew</b> A/B/C en las fallas para habilitar la comparativa.
          </p>
        ) : (
          <div style={{ width: "100%", height: 400 }} data-testid="pareto-cmp-chart">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 5, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={90} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }}
                       label={{ value: "Horas de downtime", angle: -90, position: "insideLeft",
                                style: { fontSize: 10, fill: "#64748B" } }} />
                <Tooltip
                  formatter={(v, name) => [`${Number(v).toFixed(1)} h`, `Cuadrilla ${name}`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="A" fill={crewColor.A} name="A" />
                <Bar dataKey="B" fill={crewColor.B} name="B" />
                <Bar dataKey="C" fill={crewColor.C} name="C" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {data?.gaps?.length > 0 && (
        <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-4"
             data-testid="pareto-cmp-gaps">
          <p className="font-heading text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
            <Sparkle size={16} weight="fill" /> Brechas más grandes entre turnos
          </p>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.gaps.slice(0, 5).map((g, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-800 font-bold">•</span>
                <span>
                  <b>{g.category}</b>: cuadrilla <b style={{ color: crewColor[g.worst_crew] }}>{g.worst_crew}</b> tiene{" "}
                  <b>{g.worst_h.toFixed(1)}h</b> vs <b>{g.best_h.toFixed(1)}h</b> de{" "}
                  <b style={{ color: crewColor[g.best_crew] }}>{g.best_crew}</b>{" "}
                  <span className="text-slate-500">(Δ {g.delta_h.toFixed(1)}h)</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data?.analysis?.length > 0 && (
        <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-4"
             data-testid="pareto-cmp-analysis">
          <p className="font-heading text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
            <Sparkle size={16} weight="fill" /> Análisis comparativo
          </p>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.analysis.map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-blue-900 font-bold">•</span>
                <span dangerouslySetInnerHTML={{ __html: a.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {data?.rows?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="pareto-cmp-table">
            <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Causa</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.A }}>A · Horas</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.A }}>A · Ev.</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.B }}>B · Horas</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.B }}>B · Ev.</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.C }}>C · Horas</th>
                <th className="text-center px-3 py-2" style={{ color: crewColor.C }}>C · Ev.</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-center px-3 py-2">Peor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => (
                <tr key={r.category}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.category}</td>
                  <td className="text-center px-3 py-2">{r.A_h.toFixed(1)}</td>
                  <td className="text-center px-3 py-2 text-slate-500">{r.A_events}</td>
                  <td className="text-center px-3 py-2">{r.B_h.toFixed(1)}</td>
                  <td className="text-center px-3 py-2 text-slate-500">{r.B_events}</td>
                  <td className="text-center px-3 py-2">{r.C_h.toFixed(1)}</td>
                  <td className="text-center px-3 py-2 text-slate-500">{r.C_events}</td>
                  <td className="text-right px-3 py-2 font-bold">{r.total_h.toFixed(1)}</td>
                  <td className="text-center px-3 py-2">
                    {r.worst_crew !== "-" && (
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold text-white"
                            style={{ background: crewColor[r.worst_crew] }}>{r.worst_crew}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function MicroKpi({ label, value, sub, accent = "blue" }) {
  const cls = {
    blue: "border-blue-200 text-blue-900 bg-blue-50",
    amber: "border-amber-200 text-amber-900 bg-amber-50",
    emerald: "border-emerald-200 text-emerald-900 bg-emerald-50",
  }[accent];
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

function DelayCatalogDialog({ open, onOpenChange, onSaved }) {
  const [values, setValues] = useState([]);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/maintenance/settings/delay-catalog").then((r) => setValues(r.data.values || []))
      .catch(() => setValues([]));
  }, [open]);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.warning("Ya existe"); return; }
    setValues([...values, v]);
    setDraft("");
  };
  const remove = (i) => setValues(values.filter((_, j) => j !== i));

  const save = async () => {
    try {
      await api.put("/maintenance/settings/delay-catalog", { values });
      toast.success("Catálogo actualizado");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="pareto-cat-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Catálogo de causas (Pareto)</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 mb-2">
          Las causas canónicas hacen matching por substring contra el texto libre de
          `root_cause` de las fallas y `description` de eventos correctivos.
        </p>
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                 placeholder="Nueva causa canónica"
                 onKeyDown={(e) => e.key === "Enter" && add()}
                 className="h-9" data-testid="pareto-cat-input" />
          <Button onClick={add} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                  data-testid="pareto-cat-add"><Plus size={14} weight="bold" /></Button>
        </div>
        <div className="mt-3 space-y-1 max-h-72 overflow-y-auto">
          {values.map((v, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-slate-50 rounded"
                 data-testid={`pareto-cat-item-${i}`}>
              <span className="text-sm text-slate-800">{v}</span>
              <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700 p-1">
                <Trash size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} className="bg-blue-900 hover:bg-blue-800"
                  data-testid="pareto-cat-save">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
